import { useMemo } from 'react';
import { Tracker } from 'usage-tracker-core';
import { LockedTrackerInstance } from 'usage-tracker-core/trackerTypes';
import { cloneTracker, cloneStandaloneTracker } from '../utils/UsageTracker';

export const NOT_USING_TRACKER =
  'WE HAVE CUSTOM TRACKING IN PLACE AND AI-EXTENSIBILITY TEAM IS AWARE';

export const useTracker = (
  trackerInstance: Tracker | typeof NOT_USING_TRACKER,
  usageTrackerLocation?: string
): {
  aiTracker: LockedTrackerInstance<{ standalone: true }> | undefined;
  legacyTracker: Tracker | undefined;
} => {
  const aiTracker = useMemo(() => {
    if (trackerInstance === NOT_USING_TRACKER) {
      return undefined;
    }
    return cloneStandaloneTracker(trackerInstance, usageTrackerLocation);
  }, [trackerInstance, usageTrackerLocation]);

  const legacyTracker = useMemo(() => {
    if (trackerInstance === NOT_USING_TRACKER) {
      return undefined;
    }
    return trackerInstance.clone({
      standalone: false,
      preserveTrackerProperties: false,
    });
  }, [trackerInstance]);

  return {
    aiTracker,
    legacyTracker,
  };
};

/** Prefer useTracker over making the tracker optional */
export const useOptionalTracker = (
  trackerInstance?: Tracker | typeof NOT_USING_TRACKER,
  appName?: string
): {
  aiTracker: LockedTrackerInstance<{ standalone: true }> | undefined;
  legacyTracker: Tracker | undefined;
} => {
  const aiTracker = useMemo(() => {
    if (trackerInstance === NOT_USING_TRACKER || !trackerInstance) {
      return undefined;
    }

    return cloneStandaloneTracker(trackerInstance, appName);
  }, [trackerInstance, appName]);

  const legacyTracker = useMemo(() => {
    if (trackerInstance === NOT_USING_TRACKER || !trackerInstance) {
      return undefined;
    }

    return cloneTracker(trackerInstance, appName);
  }, [trackerInstance, appName]);

  return {
    aiTracker,
    legacyTracker,
  };
};
